import { Box, Button, Typography } from "@mui/material";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";

import { useCreateRuleRulesPostMutation } from "api";
import { SelectInput, SwitchInput, TextInput } from "components/forms";
import { IRuleSoundLevel } from "interfaces/rules.interface";

import { RulePresetSoundLevel as config } from "../common/RulePresetMinMaxValues";
import {
  boundedNumberSchema,
  configurationSchema,
  timeRestrictionSchema,
} from "../common/schemas";
import {
  epochConditionsFromApi,
  epochConditionsToApi,
} from "../common/unitConversion";
import { TimeRestrictionInput } from "../components/TimeRestrictionInput";

const conditionsSchema = z
  .object({
    initial_presence_time_epochs_gte: boundedNumberSchema(
      config.initial_presence_time_epochs_gte,
    ),
    sound_level_gte: boundedNumberSchema(config.sound_level_gte),
  })
  .nullable()
  .default(null);

const ruleSoundLevelSchema = z.object({
  time_restriction: timeRestrictionSchema,
  conditions_preset: z.enum(["default", ""]).nullable().default("default"),
  conditions: conditionsSchema,
  configuration: configurationSchema,
});

type RuleSoundLevelSchema = z.infer<typeof ruleSoundLevelSchema>;

type RuleSoundLevelFormProps = {
  entityId: string;
  presets: { id: string; name: string }[];
  defaultValues: IRuleSoundLevel | null;
  disabled: boolean;
  onClose: () => void;
};

export function RuleSoundLevelForm({
  entityId,
  presets,
  defaultValues,
  disabled,
  onClose,
}: RuleSoundLevelFormProps) {
  const { t } = useTranslation();

  const [createRule] = useCreateRuleRulesPostMutation();

  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: ruleSoundLevelSchema.safeParse(
      defaultValues
        ? {
            ...defaultValues,
            conditions: epochConditionsFromApi(defaultValues.conditions),
          }
        : {},
    ).data,
    resolver: zodResolver(ruleSoundLevelSchema),
  });

  const onSubmit = async (data: RuleSoundLevelSchema) => {
    await createRule({
      ruleCreateModels: {
        ...data,
        type: "subject.session.epochs.sound-level",
        entity_id: entityId,
        description: null,
        enabled: true,
        conditions_preset: data.conditions_preset || null,
        conditions: epochConditionsToApi(data.conditions),
      },
    });

    onClose();
  };

  const presetId = watch("conditions_preset");

  return (
    <Box
      component={"form"}
      onSubmit={handleSubmit(onSubmit)}
      sx={{ display: "flex", flexDirection: "column", flexGrow: 1, gap: 2 }}
    >
      <SelectInput
        control={control}
        label={t("preset")}
        fieldName={"conditions_preset"}
        options={presets}
        placeholder={t("custom")}
        optionLabelField={"name"}
        disabled={disabled}
        onChange={(event) => {
          if (event.target.value.length === 0) {
            const value = conditionsSchema.parse({
              ok_to_alert: {},
              alert_to_ok: {},
            });

            setValue("conditions", value);
          } else {
            setValue("conditions", null);
          }
        }}
      />
      {!presetId && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            padding: 1,
            border: 1,
            borderRadius: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant={"bodyLargeMedium"}>
            {t("send_notification_message")}
          </Typography>
          <Box
            sx={{
              padding: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <TextInput
              control={control}
              disabled={disabled}
              fieldName={"conditions.initial_presence_time_epochs_gte"}
              label={t("minimum_time_presence_is")}
              type={"number"}
              unit={t("mins")}
              min={config.initial_presence_time_epochs_gte.exclusiveMinimum}
              max={config.initial_presence_time_epochs_gte.exclusiveMaximum}
            />
            <TextInput
              control={control}
              disabled={disabled}
              fieldName={"conditions.sound_level_gte"}
              label={
                t("and") + " " + t("sound_level_is_greater_than_or_equal_to")
              }
              type={"number"}
              unit={t("dBA")}
              min={config.sound_level_gte.exclusiveMinimum}
              max={config.sound_level_gte.exclusiveMaximum}
            />
          </Box>
          <Typography variant={"bodyLargeMedium"}>
            {t("send_ok_message_only_through_SMS")}
          </Typography>
          <Box
            sx={{
              padding: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <SwitchInput
              control={control}
              disabled={disabled}
              fieldName={"configuration.execute_actions_on_alert_to_ok"}
              label={t("Enabled")}
            />
          </Box>
        </Box>
      )}
      <TimeRestrictionInput
        control={control}
        setValue={setValue}
        disabled={disabled}
      />
      <Box sx={{ flexGrow: 1 }} />
      {!disabled && <Button type={"submit"}>{t("Create")}</Button>}
    </Box>
  );
}
